import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import { entitlementsQueryOptions } from "@/app/enduser/api/get-entitlements";
import { buildEnduserLoginUrl } from "@/app/enduser/utils";
import { isHttpError } from "@/lib/http-error";

const UNAUTHORIZED_STATUS = 401;

/**
 * End-user self-service surface. This subtree is a sibling of `_authenticated`,
 * NOT a child: end-users are not Arcade accounts and hold no operator session or
 * permissions. It must never touch operator Better Auth or the org/project
 * capability guards. Auth here is the org IdP session carried by the
 * `arcade_eu_session` cookie, which the BFF end-user proxy validates.
 */
const searchSchema = z.object({
  project_id: z.string().min(1),
  user_source_id: z.string().min(1),
});

export const Route = createFileRoute("/eu")({
  validateSearch: zodValidator(searchSchema),
  beforeLoad: async ({ context, search, location }) => {
    // Probe the end-user session. A 401 means there is no IdP session yet, so
    // bounce the browser to the BFF login endpoint with a return_to back here.
    try {
      await context.queryClient.ensureQueryData(entitlementsQueryOptions());
    } catch (error) {
      if (isHttpError(error) && error.status === UNAUTHORIZED_STATUS) {
        throw redirect({
          href: buildEnduserLoginUrl({
            projectId: search.project_id,
            userSourceId: search.user_source_id,
            returnTo: `${window.location.origin}${location.href}`,
          }),
        });
      }
      throw error;
    }
  },
  component: EnduserLayout,
});

// Standalone shell for the end-user surface: it renders outside the operator app
// shell, so it owns its own centered, padded, max-width container (otherwise the
// content sits flush against the viewport edge).
function EnduserLayout() {
  return (
    <div className="min-h-svh bg-background">
      <div className="mx-auto max-w-3xl px-6 py-10 sm:px-8">
        <Outlet />
      </div>
    </div>
  );
}
